// Bus search as URL query values, shared by the search widget, results and checkout pages.
// Query: from / to (station names from the suggestions, e.g. "Hyderabad"), date (journey date).
import { addDaysIso, fmtDay, todayIso } from '../../shared/data/common';

export const MAX_SEATS = 6;
export const MAX_DAYS_AHEAD = 120;

/** Search values from URL query parameters, with defaults for anything missing. */
export function parseBusQuery(q) {
  return {
    from: (q.from || '').trim(),
    to: (q.to || '').trim(),
    date: q.date && q.date >= todayIso() ? q.date : addDaysIso(1),
  };
}

export const toBusParams = (s) => ({ from: s.from, to: s.to, date: s.date });

export const busQueryString = (s) => new URLSearchParams(toBusParams(s)).toString();

export const toSearchRequest = (s) => ({ source: s.from, destination: s.to, journeyDate: s.date });

/** Problems the API would reject, caught before searching. */
export function searchProblem(s) {
  if (!s.from) return 'Choose where you are leaving from.';
  if (!s.to) return 'Choose where you are going.';
  if (s.from.toLowerCase() === s.to.toLowerCase()) return 'From and To must be different cities.';
  if (s.date > addDaysIso(MAX_DAYS_AHEAD)) return `Buses can be booked up to ${MAX_DAYS_AHEAD} days ahead.`;
  return null;
}

/** "2026-10-28T21:35:00" → "9:35 PM". Times are local to the route and arrive without a zone. */
export function timeLabel(iso) {
  const [h, m] = String(iso || '').slice(11, 16).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export const dateOf = (iso) => String(iso || '').slice(0, 10);

/** "Wed, 28 Oct 2026, 9:35 PM". */
export const whenLabel = (iso) => `${fmtDay(dateOf(iso))}, ${timeLabel(iso)}`;

/** Days after the first date: 1 for an overnight arrival. */
export const daysLater = (fromIso, toIso) => Math.round((new Date(dateOf(toIso)) - new Date(dateOf(fromIso))) / 864e5);

/** 525 → "8h 45m". */
export function durationLabel(minutes) {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
}

// Departure time filter: [key, label, first hour, last hour].
export const departureSlots = [
  ['early', 'Before 6 AM', 0, 6],
  ['morning', '6 AM – 12 PM', 6, 12],
  ['afternoon', '12 PM – 6 PM', 12, 18],
  ['night', 'After 6 PM', 18, 24],
];

export const slotOf = (iso) => {
  const hour = Number(String(iso).slice(11, 13));
  return departureSlots.find(([, , from, to]) => hour >= from && hour < to)?.[0];
};

export const seatsLabel = (n) => `${n} seat${n === 1 ? '' : 's'}`;
